import type { Query } from "@tanstack/react-query";

/** Mark prefetch / eager warming so the portal does not treat it as a wait. */
export const BACKGROUND_QUERY_META = { background: true } as const;

function isBackgroundQuery(query: Query): boolean {
  return query.meta?.background === true;
}

/**
 * Whether a running fetch is work the user is actively waiting on.
 *
 * A query key that has never resolved real data has nothing to render, so the
 * screen is blocked until it lands: first paint, a changed filter, a new page,
 * and opening a details panel all fetch such a key. Once a key holds data, any
 * further fetch (cache invalidation after a write, polling, refetch on window
 * focus) only replaces content that is already on screen, so surfacing it would
 * interrupt the user for something they never asked for.
 *
 * Placeholder data from `keepPreviousData` deliberately does not count as real
 * data, which keeps filter and page changes in the foreground even though stale
 * rows stay visible while the new page loads.
 *
 * Prefetch of adjacent pages uses {@link BACKGROUND_QUERY_META} so eager warming
 * never greys a list the user can already use.
 */
export function isForegroundQueryFetch(query: Query): boolean {
  if (isBackgroundQuery(query)) return false;
  return query.state.dataUpdatedAt === 0;
}

/** {@link isForegroundQueryFetch} for a `useQuery` result. */
export function isForegroundFetch(result: {
  isFetching: boolean;
  dataUpdatedAt: number;
}): boolean {
  return result.isFetching && result.dataUpdatedAt === 0;
}
